import { restCall, restErrorStatus } from 'shared/lib/http/restCall'
import settings from 'src/config/settings'

import { IScreeningVerdict, TScreening, TScreeningRerun } from '../model/types'

export type TScreeningResultAnswer =
  | { kind: 'found'; verdict: IScreeningVerdict }
  | { kind: 'none' }
  | { kind: 'refused' }

export type TScreeningRerunAnswer =
  | { kind: 'answered'; rerun: TScreeningRerun }
  | { kind: 'none' }
  | { kind: 'refused' }

interface IScreeningResultBody {
  status: 'success'
  data: IScreeningVerdict
}

interface IScreeningRerunBody {
  status: 'success'
  data: TScreeningRerun
}

const BASE = `${settings.httpsServices.onboarding}/api/v1/bp`
const NOT_FOUND = 404
const FORBIDDEN = 403
const UNREADABLE_BODY = 'The screening service answered with an unreadable body'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isRecordList = (value: unknown): boolean => Array.isArray(value) && value.every(isRecord)

const isMatchedEntry = (entry: unknown): boolean =>
  isRecord(entry) &&
  isRecordList(entry.hits) &&
  isRecordList(entry.fields) &&
  (entry.compare === undefined || isRecordList(entry.compare))

const isListRow = (row: unknown): boolean => isRecord(row) && Array.isArray(row.alternativeNames)

const isRerunEntry = (entry: unknown): boolean =>
  isRecord(entry) && isListRow(entry.listed) && isRecordList(entry.compare)

const isScreenedRerun = (data: Record<string, unknown>): boolean =>
  data.outcome === 'screened' &&
  typeof data.tier === 'string' &&
  typeof data.list === 'string' &&
  typeof data.screenedEntries === 'number' &&
  typeof data.total === 'number' &&
  isRecord(data.submitted) &&
  Array.isArray(data.entries) &&
  data.entries.every(isRerunEntry)

const isNotScreenedRerun = (data: Record<string, unknown>): boolean =>
  data.outcome === 'not-screened' && typeof data.reason === 'string'

const isScreeningRerunBody = (body: unknown): body is IScreeningRerunBody =>
  isRecord(body) &&
  body.status === 'success' &&
  isRecord(body.data) &&
  (isScreenedRerun(body.data) || isNotScreenedRerun(body.data))

const isScreeningResultBody = (body: unknown): body is IScreeningResultBody =>
  isRecord(body) &&
  body.status === 'success' &&
  isRecord(body.data) &&
  typeof body.data.tier === 'string' &&
  isRecord(body.data.matches) &&
  Array.isArray(body.data.matches.entries) &&
  body.data.matches.entries.every(isMatchedEntry)

export async function fetchScreeningResult(
  requestId: number,
  screening: TScreening
): Promise<TScreeningResultAnswer> {
  let body: unknown

  try {
    body = await restCall<unknown>({
      method: 'get',
      url: `${BASE}/requests/${requestId}/screening/${screening}`,
    })
  } catch (e: unknown) {
    const status = restErrorStatus(e)

    if (status === NOT_FOUND) {
      return { kind: 'none' }
    }

    if (status === FORBIDDEN) {
      return { kind: 'refused' }
    }

    throw e
  }

  if (!isScreeningResultBody(body)) {
    throw new Error(UNREADABLE_BODY)
  }

  return { kind: 'found', verdict: body.data }
}

export async function rerunScreening(
  requestId: number,
  screening: TScreening
): Promise<TScreeningRerunAnswer> {
  let body: unknown

  try {
    body = await restCall<unknown>({
      method: 'get',
      url: `${BASE}/requests/${requestId}/screening/${screening}/rerun`,
    })
  } catch (e: unknown) {
    const status = restErrorStatus(e)

    if (status === NOT_FOUND) {
      return { kind: 'none' }
    }

    if (status === FORBIDDEN) {
      return { kind: 'refused' }
    }

    throw e
  }

  if (!isScreeningRerunBody(body)) {
    throw new Error(UNREADABLE_BODY)
  }

  return { kind: 'answered', rerun: body.data }
}
